import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Badge } from "reactstrap";

import { Table, Spinner, Modal, ExportToCsv, TableDashboard } from "@atoms";
import { pagination } from "@config/conf";
import { formatCurrency, getPagination } from "@containers/components/helpers";
import useApiFetch from "@hooks/useApiFetch";
import useCsvExportButton from "@hooks/useCsvExportButton";
import useQueryParams from "@hooks/useQueryParams";
import { formatDate } from "@lib/date";
import { formatFixedDigits } from "@lib/number";

import PurchasesWithdrawalsSearch from "./PurchasesWithdrawalsSearch";

const queryParam = [
  "search",
  "status",
  "custodian",
  "pageSize",
  "sortWfNumber",
  "sortWfStartDate",
  "sortWfStatus",
  "page",
  "dateRangeInput",
  "effectiveStartDate",
  "effectiveEndDate",
];

const initialModalState = {
  rejectModal: false,
  submitModal: false,
  confirmSubmitModal: false,
  alertModal: false,
  exportingId: "",
  wfID: "",
};

const PurchasesWithdrawals = () => {
  const { post, get, isPending } = useApiFetch();
  const history = useHistory();
  const { getQueryParams, buildQueryParams } = useQueryParams();

  const {
    search,
    status: purchaseAndWithdrawalsStatus,
    custodian: custodians,
    pageSize: pageSizePath,
    sortWfNumber,
    sortWfStartDate,
    sortWfStatus,
    page,
    dateRangeInput,
    effectiveStartDate,
    effectiveEndDate,
  } = getQueryParams(queryParam);

  const form = useForm({
    defaultValues: {
      searchWorkflow: search,
      workflowStatus: purchaseAndWithdrawalsStatus || "In Progress",
      custodianID: custodians,
      dateRange: dateRangeInput || "AllEntries",
      effectiveStartDate,
      effectiveEndDate,
    },
  });

  const [pageSize, setPageSize] = useState(pageSizePath || pagination.length);
  const [submitSearch, setSubmitSearch] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [tableState, setTableState] = useState(initialModalState);
  const [fetchData, setFetchData] = useState({});

  // Added a new usestates to store the dictionary of ids
  const [workflowDetailIDs, setWorkflowDetailIDs] = useState({});
  const [unfilteredworkflowDetailIDs, setUnfilteredWorkflowDetailIDs] =
    useState({});

  //new function to handle checkbox click
  const handleCheckboxChange = (id, subId, isChecked) => {
    setWorkflowDetailIDs((prev) => {
      const current = prev[id] ?? [];

      if (isChecked) {
        if (!current.includes(subId)) {
          return {
            ...prev,
            [id]: [...current, subId],
          };
        }
        return prev;
      } else {
        return {
          ...prev,
          [id]: current.filter((guid) => guid !== subId),
        };
      }
    });
  };

  //Logic to handle preselection of checkboxes
  const handlePreselect = (status, id) => {
    setWorkflowDetailIDs((prev) => ({
      ...prev,
      [id]: status ? unfilteredworkflowDetailIDs[id] : [],
    }));
  };

  const isAllSelected = (flowId) => {
    const selected = workflowDetailIDs[flowId] ?? [];
    return (
      selected.length > 0 &&
      selected.length === unfilteredworkflowDetailIDs[flowId].length &&
      unfilteredworkflowDetailIDs[flowId].every((id) => selected.includes(id))
    );
  };

  //Logic to initialize data to the workflowDetailIDs dictionary
  const initialSetWorkflowDetailIDs = (response) => {
    var workFlowDict = {};
    response.data.forEach((flow) => {
      const eligibleSubFlows = flow.workflowData
        .filter(
          (subflow) => subflow.processed === false && subflow.result !== "Error"
        )
        .map((subflow) => subflow.workflowDetailID);

      workFlowDict[flow.workflowID] = eligibleSubFlows;
    });
    setWorkflowDetailIDs(workFlowDict);
  };

  const initialSetUnfilteredWorkflowDetailIDs = (response) => {
    var workFlowDict = {};
    response.data.forEach((flow) => {
      const eligibleSubFlows = flow.workflowData
        .filter((subflow) => subflow.processed === false)
        .map((subflow) => subflow.workflowDetailID);

      workFlowDict[flow.workflowID] = eligibleSubFlows;
    });
    setUnfilteredWorkflowDetailIDs(workFlowDict);
  };

  //this pandwWorkflowColumn was moved inside the PuchaseWithdrawals function
  //to add the checkbox onchange functionality
  const pandwWorkflowColumn = [
    {
      id: "workflowDetailID",
      header: ({ table }) => {
        const id = table.getRowModel().rows[0].original.workflowID;
        return (
          <input
            type="checkbox"
            checked={isAllSelected(id)}
            onChange={(e) => {
              const rowId = table.getRowModel().rows[0].original.workflowID;
              handlePreselect(e.target.checked, rowId);

              table.toggleAllPageRowsSelected(e.target.checked);
            }}
          />
        );
      },
      accessorKey: "workflowDetailID",
      size: 50,
      cell: (tableRow) => {
        const { workflowDetailID } = tableRow.row.original || "";
        const { workflowID } = tableRow.row.original || "";
        const { processed } = tableRow.row.original || false;

        return processed ? (
          <input type="checkbox" disabled />
        ) : (
          <input
            type="checkbox"
            checked={
              workflowDetailIDs[workflowID]?.includes(workflowDetailID) ?? false
            }
            onChange={(e) =>
              handleCheckboxChange(
                workflowID,
                workflowDetailID,
                e.target.checked
              )
            }
          />
        );
      },
      alignRight: true,
    },
    {
      id: "reAccountNumber",
      header: "RE Account #",
      accessorKey: "reAccountNumber",
      alignRight: true,
      size: 75,
    },
    {
      id: "result",
      header: "Result",
      accessorKey: "result",
      alignRight: true,
      size: 75,
    },
    {
      id: "activityDate",
      header: "Posted",
      accessorFn: (row) => formatDate(row?.activityDate, "MM/DD/YY"),
      alignRight: true,
      size: 75,
    },
    {
      id: "activityAmount",
      header: "Amount",
      cell: (tableRow) => {
        const { activityAmount } = tableRow.row.original || {};
        return (
          <>
            {activityAmount < 0 ? (
              <span className="text-danger">
                {"(" + formatCurrency(Math.abs(activityAmount), 2) + ")"}
              </span>
            ) : (
              formatCurrency(activityAmount, 2)
            )}
          </>
        );
      },
      alignRight: true,
    },
    {
      id: "childWorkflowNumber",
      header: "WF#",
      accessorFn: (row) =>
        row?.childWorkflowNumber
          ? formatFixedDigits(row?.childWorkflowNumber, 12)
          : "-",
      alignRight: true,
    },
    {
      id: "workflowStatus",
      header: "Status",
      accessorFn: (row) =>
        `${row?.childWorkflowStatus || ""} / ${
          row?.childWorkflowSubStatus || ""
        }`,
      alignRight: true,
    },
    {
      id: "resultMessage",
      header: "Result Message",
      accessorKey: "resultMessage",
    },
  ];

  const PandWWorkflowLogTable = (subTableProps) => {
    var { workflowData = [] } = subTableProps.row.original || {};
    workflowData = workflowData.map((item) => ({
      ...item,
      workflowID: subTableProps.row.original.workflowID,
    }));

    return (
      <Table columns={pandwWorkflowColumn} data={workflowData} hasNestedTable />
    );
  };

  const { offset, pageNumber } = getPagination(pageSize, page);
  const { recordCount = 0, data = [] } = fetchData || {};

  const {
    csvClickRef,
    csvData: { data: exportData = [] },
    getExport,
    loadingResponse,
  } = useCsvExportButton();

  const { getValues } = form;
  const {
    searchWorkflow,
    workflowStatus,
    custodianID,
    dateRange,
    effectiveStartDate: startDate,
    effectiveEndDate: endDate,
  } = getValues();

  const {
    alertModal,
    confirmSubmitModal,
    exportingId,
    rejectModal,
    submitModal,
    wfID,
  } = tableState;

  const orderBy = useMemo(() => {
    if (sortWfNumber) {
      return `Workflows.WorkflowNumber ${sortWfNumber.toUpperCase()}`;
    }
    if (sortWfStartDate) {
      return `Workflows.WorkflowStartDate ${sortWfStartDate.toUpperCase()}, Workflows.WorkflowNumber DESC`;
    }
    if (sortWfStatus) {
      return `Workflows.Status ${sortWfStatus.toUpperCase()}, Workflows.WorkflowNumber DESC`;
    }
    return "";
  }, [sortWfNumber, sortWfStartDate, sortWfStatus]);

  const updateState = useCallback((field, value) => {
    setTableState((state) => ({
      ...state,
      [field]: value,
    }));
  }, []);

  const handleCsvExport = useCallback((workflowID) => {
    updateState("exportingId", workflowID);
    getExport(`operations/pandw/${workflowID}`);
  }, []);

  const queryParams = buildQueryParams({
    search: searchWorkflow,
    status: workflowStatus,
    custodian: custodianID,
    sortWfNumber,
    sortWfStartDate,
    sortWfStatus,
    dateRangeInput: dateRange,
    effectiveStartDate: startDate,
    effectiveEndDate: endDate,
    pageSize,
  });

  const getPurchaseAndWithdrawals = useCallback(async () => {
    const params = buildQueryParams({
      Limit: pageSize,
      Offset: offset,
      OrderBy: orderBy,
      search: searchWorkflow,
      status: workflowStatus,
      custodianID,
      dateRangeRequested: dateRange,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
    });

    const response = await get(`operations/pandw?${params}`);
    setFetchData(response);
    initialSetWorkflowDetailIDs(response);
    initialSetUnfilteredWorkflowDetailIDs(response);
  }, [offset, orderBy, pageSize, submitSearch, refresh]);

  useEffect(() => {
    if (submitSearch) {
      history.push(`/purchases-and-withdrawals?page=1&${queryParams}`);
    } else {
      getPurchaseAndWithdrawals();
    }
    setRefresh(false);
    setSubmitSearch(false);
  }, [getPurchaseAndWithdrawals, refresh, submitSearch]);

  const handleAction = useCallback(
    (workflowID, actionType) => {
      if (isPending) {
        updateState("alertModal", true);
        return false;
      }
      updateState("wfID", workflowID);
      if (actionType === "submit") {
        updateState("submitModal", !submitModal);
      } else if (actionType === "reject") {
        updateState("rejectModal", !rejectModal);
      }
    },
    [isPending, rejectModal, submitModal]
  );

  const pandwColumn = useMemo(
    () => [
      {
        id: "expanded-chevron",
        header: "Select",
        size: 0,
        cell: (tableRow) => {
          const { expandedRowIds, row } = tableRow || {};
          const className = `lnr ${
            expandedRowIds?.includes(row.id)
              ? "lnr-chevron-down"
              : "lnr-chevron-right"
          }`;
          return (
            <div className="expanded_chevron w-full text-right mt-1">
              <i className={className} />
            </div>
          );
        },
        alignRight: true,
      },
      {
        id: "alert",
        header: "",
        size: 0,
        cell: (tableRow) => {
          const { workflowData } = tableRow.row.original || {};
          const alert = workflowData.some((item) => item.result === "Error");

          return alert ? (
            <div
              className="bg-danger text-danger mb-0 rounded-circle mt-2"
              style={{
                height: "10px",
                width: "10px",
              }}
            />
          ) : null;
        },
      },
      {
        id: "wfNumber",
        enableSorting: true,
        header: "WF#",
        accessorFn: (row) => formatFixedDigits(row?.workflowNumber, 12),
        alignRight: true,
        size: 75,
      },
      {
        id: "wfStartDate",
        enableSorting: true,
        header: "Start Date",
        accessorFn: (row) => formatDate(row?.workflowStartDate, "MM/DD/YY"),
        alignCenter: true,
      },
      {
        id: "wfStatus",
        header: "Status",
        accessorFn: (row) => `${row?.status} / ${row?.subStatus}`,
      },
      {
        id: "custodianName",
        header: "Custodian",
        accessorKey: "custodianName",
      },
      {
        id: "totalAcceptAndReject",
        header: <span className="column-start">A / R</span>,
        size: 100,
        cell: (tableRow) => {
          const { totAccepted, totRejected } = tableRow?.row?.original || {};
          return (
            <div className="column-start">
              {`${totAccepted || 0} / ${totRejected || 0}`}
            </div>
          );
        },
      },
      {
        id: "totalPurchaseAndWithdrawalsReceived",
        header: <span className="column-end">P / W Received</span>,
        cell: (tableRow) => {
          const { totPurchasesReceived, totWithdrawalsReceived } =
            tableRow?.row?.original || {};
          return (
            <div className="column-end">
              {formatCurrency(Math.abs(totPurchasesReceived), 2)} /{" "}
              <span className="text-danger">
                ({formatCurrency(Math.abs(totWithdrawalsReceived), 2)})
              </span>
            </div>
          );
        },
        alignRight: true,
      },
      {
        id: "totalPurchaseAndWithdrawals",
        header: <span className="column-end">P / W Accepted</span>,
        cell: (tableRow) => {
          const { totPurchases, totWithdrawals } =
            tableRow?.row?.original || {};
          return (
            <div className="column-end">
              {formatCurrency(Math.abs(totPurchases), 2)} /{" "}
              <span className="text-danger">
                ({formatCurrency(Math.abs(totWithdrawals), 2)})
              </span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: (tableRow) => {
          const {
            status,
            subStatus,
            workflowID,
            workflowNumber,
            workflowData,
          } = tableRow.row.original || {};
          const isExporting = exportingId === workflowID;

          return (
            <div className="flex justify-content-end pr-2 my-1">
              {status === "In Progress" &&
              (subStatus === "Accepted" || subStatus === "Pending") ? (
                <>
                  <Badge
                    color="danger"
                    className="pointer mr-1 h-max-content"
                    onClick={() => handleAction(workflowID, "reject")}
                  >
                    Reject
                  </Badge>
                  <Badge
                    color="success"
                    className="pointer mr-1 h-max-content"
                    onClick={() => handleAction(workflowID, "submit")}
                  >
                    Submit
                  </Badge>
                </>
              ) : null}
              <ExportToCsv
                csvRef={csvClickRef}
                fileName={formatFixedDigits(workflowNumber, 12)}
                getCsvExport={() => handleCsvExport(workflowID)}
                loadingResponse={isExporting && loadingResponse}
                tableCellSpecifier={{ hasExactCell: isExporting }}
                component="badge"
                csvData={exportData}
              />
            </div>
          );
        },
      },
    ],
    [exportData, exportingId, handleAction, loadingResponse]
  );

  //changed the submission and rejection functions just to print the values
  const handleRejectWorkflow = useCallback(async () => {
    var workflowDetailIDList = workflowDetailIDs[wfID] || [];

    const body = workflowDetailIDList.map((value) => ({
      workflowDetailId: value,
    }));

    await post(`operations/pandw/processworkflowdetails/${wfID}/Reject`, body);
    setRefresh(true);
  }, [wfID, refresh, workflowDetailIDs]);

  const handleSubmitWorkflow = useCallback(async () => {
    var workflowDetailIDList = workflowDetailIDs[wfID] || [];
    const body = workflowDetailIDList.map((value) => ({
      workflowDetailId: value,
    }));

    await post(`operations/pandw/processworkflowdetails/${wfID}/Submit`, body);
    setRefresh(true);
  }, [wfID, refresh, workflowDetailIDs]);

  const modals = useMemo(
    () => [
      {
        modalName: "reject-modal",
        isModalOpen: rejectModal,
        setIsModalOpen: (e) => updateState("rejectModal", e),
        content: "Are you sure you wish to reject this workflow?",
        buttonProps: [
          {
            name: "Cancel",
            color: "danger",
            onClick: () => updateState("rejectModal", !rejectModal),
          },
          {
            name: "Confirm",
            color: "primary",
            onClick: () => {
              handleRejectWorkflow();
              updateState("rejectModal", false);
            },
          },
        ],
      },
      {
        modalName: "submit-modal",
        isModalOpen: submitModal,
        setIsModalOpen: (e) => updateState("submitModal", e),
        content: "Are you sure you wish to submit this workflow?",
        buttonProps: [
          {
            name: "Cancel",
            color: "danger",
            onClick: () => updateState("submitModal", !submitModal),
          },
          {
            name: "Confirm",
            color: "primary",
            onClick: () => {
              updateState("confirmSubmitModal", !confirmSubmitModal);
              updateState("submitModal", false);
            },
          },
        ],
      },
      {
        modalName: "confirm-submit-modal",
        isModalOpen: confirmSubmitModal,
        setIsModalOpen: (e) => updateState("confirmSubmitModal", e),
        content: "Please confirm funds have been received and verified?",
        buttonProps: [
          {
            name: "Cancel",
            color: "danger",
            onClick: () =>
              updateState("confirmSubmitModal", !confirmSubmitModal),
          },
          {
            name: "Confirm",
            color: "primary",
            onClick: () => {
              handleSubmitWorkflow();
              updateState("confirmSubmitModal", false);
            },
          },
        ],
      },
      {
        modalName: "alert-modal",
        isModalOpen: alertModal,
        setIsModalOpen: (e) => updateState("alertModal", e),
        content:
          "Another Submit or Reject action is in process. Please wait before submitting another one.",
        buttonProps: [
          {
            name: "Ok",
            color: "danger",
            onClick: () => () => updateState("alertModal", !alertModal),
          },
        ],
      },
    ],
    [
      alertModal,
      confirmSubmitModal,
      handleRejectWorkflow,
      handleSubmitWorkflow,
      rejectModal,
      submitModal,
    ]
  );

  const pageProps = {
    total: recordCount,
    pageSize,
    setPageSize,
    pageNumber,
  };

  return (
    <TableDashboard
      pageTitle="Purchases &amp; Withdrawals"
      setRefresh={setRefresh}
    >
      {modals?.map((modal) => {
        const { buttonProps, content, isModalOpen, setIsModalOpen, modalName } =
          modal || {};
        return (
          <Modal
            key={modalName}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            content={content}
            modalButtons={buttonProps}
          />
        );
      })}
      <Spinner requesting={isPending}>
        <PurchasesWithdrawalsSearch
          setSubmitSearch={setSubmitSearch}
          form={form}
        />
        <Table
          columns={pandwColumn}
          data={data}
          renderNestedTable={PandWWorkflowLogTable}
          pageProps={pageProps}
        />
      </Spinner>
    </TableDashboard>
  );
};

export default PurchasesWithdrawals;
